import React, { useEffect, useRef, useState } from 'react'
import { FilePond } from 'react-filepond'
import axios from 'axios'
import classNames from 'classnames'
import { Ace } from '../../components'
import { useQuery } from 'react-query'
import { produce } from 'immer'

import { ReactComponent as Spinner } from '../../images/icons/spinner.svg'
import { queryClient } from '../../config'
import { toast } from 'react-toastify'

const ImportTemplates = () => {
  const fileRef = useRef(null)
  const [templates, setTemplates] = useState([])
  const [hasChanges, setHasChanges] = useState(false)
  const [inProgress, setProgress] = useState(false)
  const {
    data,
    isLoading: isLoadingTemplates,
    refetch,
  } = useQuery('/api/migration/import_templates',
    async () => {
      const { data } = await axios.get('/api/migration/import_templates')
      return data
    }, {
      retry: false,
    })

  useEffect(() => {
    if (data) {
      setTemplates(data)
    }
  }, [data])

  const process = async (
    fieldName, file, metadata, load, error, progress, abort) => {
    const CancelToken = axios.CancelToken
    const source = CancelToken.source()
    const { data: url } = await axios.get(
      '/api/migration/presigned_upload_template?fileName=' + file.name)

    await axios.put(url, file, {
      cancelToken: source.token,
      onUploadProgress: (e) => {
        progress(e.lengthComputable, e.loaded, e.total)
      },
      headers: {
        'Content-Type': 'application/octet-stream',
      },
      transformRequest: [
        (data, headers) => {
          if (headers.setAuthorization) {
            headers.setAuthorization(undefined)
          } else if (headers.common && headers.common.Authorization) {
            delete headers.common.Authorization
          }
          return data
        }],
    })

    const { body: res } = await axios.post(
      '/api/migration/template_extract_fields?fileName=' + file.name)
    load('Uploaded')
    if (typeof res === 'string' && res.startsWith('ERROR:')) {
      // an error occurred when trying to extract fields from the uploaded DOCX...
      // display to user (todo: where?)
      toast.error(
        <div>
          {res}
        </div>, {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 5000,
        })
    }

    setTemplates(produce(draft => {
      draft.push({ fileName: file.name, templateName: '' })
    }))
    queryClient.removeQueries({ queryKey: '/api/migration/map_fields' })
    queryClient.setQueryData('/api/migration/import_templates',
      produce(data, draft => {
        draft.push({ fileName: file.name, templateName: '' })
      }))

    if (fileRef.current) {
      setTimeout(() => {
        fileRef.current.removeFile(file)
      }, 500)
    }
  }

  const onSave = async () => {
    setProgress(true)
    try {
      await axios.post('/api/migration/import_templates',
        templates.filter(_t => _t.isUpdate).
          map(({ templateName, fileName }) => {
            return ({
              templateName, fileName,
            })
          }))
      await refetch()
      setHasChanges(false)
    } catch (e) {
      console.error(e)
    } finally {
      setProgress(false)
    }
  }

  const onChangeTemplateName = (index, value) => {
    if (inProgress) {
      return
    }
    setTemplates(produce(draft => {
      draft[index].templateName = value
      draft[index].isUpdate = true
    }))
    setHasChanges(true)
  }

  return (
    <div className={'row'}>
      <div className={'col-4'}>
        <label htmlFor={'addTemplates'} className={'d-inline'}>Upload
          HotDocs Templates (.docx)
        </label>
        <FilePond
          ref={fileRef}
          id={'uploadTemplates'}
          allowMultiple={true}
          name={'templates'}
          fileRef={fileRef}
          server={{
            process,
          }}
          credits={{ label: '', url: '' }}
        />
      </div>
      {isLoadingTemplates && (<div className={'col-8'}>Loading...</div>)}
      {!isLoadingTemplates && (<div className={'col-8'}>
        <label htmlFor={'templates'} className={'d-inline'}>
          Working Templates:
        </label>
        <div className={'row knackly-plain py-2'}>
          <div className={'col-4'}><label
            className={'d-inline'}>HotDocs File Name</label></div>
          <div className={'col-4'}><label
            className={'d-inline'}>New Template Name</label></div>
          <div className="col-4"/>
          <div className={'col-12'}>
            {templates.map(
              (item, index) => (<div className={'row'} key={item.fileName}>
                <div className={'col-4'}>
                  <label
                    className={'d-block my-3'}>{item.fileName}</label>
                </div>
                <div className={'col-4'}>
                  <div className={classNames(
                    'knackly-plain rounded-0 py-2 form-control TemplateWrapper my-1')}>
                    <Ace mode={'kexpr'}
                         value={item.templateName}
                         onChange={(_v) => onChangeTemplateName(index, _v)}
                         className={'border-0'}
                         maxLines={Infinity}
                         isExpression={true}/>
                  </div>
                </div>
                <div className="col-4"/>
              </div>))}
          </div>
          {hasChanges && (<div className={'col-3'}>
            <button
              className={'btn btn-secondary btn-block p-1 btn-theme mt-2 mb-0'}
              disabled={inProgress}
              onClick={onSave}
            >
              {inProgress ? <Spinner className={'rot'} style={{
                width: 30,
                top: -4,
                position: 'relative',
              }}/> : 'Save Changes'}
            </button>
          </div>)}
        </div>
      </div>)}
    </div>
  )
}

export default ImportTemplates
